import { observable, action, makeObservable, runInAction } from 'mobx';
import api from 'api';
import axios from 'axios';

import { IColor, IPaginationResponse, IProps } from 'types/types';

interface IError {
  isOk: boolean;
  msg: string;
}

class ColorsStore {
  @observable
  colors: Array<IColor> | IPaginationResponse<IColor> = [];

  @observable
  color: IColor | null = null;

  @observable
  loading: boolean = false;

  constructor() {
    makeObservable(this);
  }

  @action
  getColors = async ({ page, paginate, perpage }: IProps): Promise<IError> => {
    this.loading = true;

    const res = {
      isOk: false,
      msg: ''
    };

    try {
      const response = await api.colors.getColorsList({
        page,
        paginate,
        perpage
      });

      runInAction(() => {
        if (response.status === 200) {
          this.colors = response.data;
          res.isOk = true;
          res.msg = 'Цвета получены';
        } else {
          console.error(
            `Error\nCode: ${response.status}\nStatus: ${response.statusText}`
          );
          this.colors = [];
          res.isOk = false;
          res.msg = response.statusText;
        }
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        res.msg =
          error.response?.data?.message ||
          error.response?.statusText ||
          error.message ||
          'Unknown error';
      } else if (error instanceof Error) {
        res.msg = error.message;
      } else {
        res.msg = 'Unknown error';
      }
      res.isOk = false;
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };

  @action
  getColor = async (id: number): Promise<IError> => {
    this.loading = true;

    const res = {
      isOk: false,
      msg: ''
    };

    try {
      const response = await api.colors.getColor(id);

      runInAction(() => {
        if (response.status === 200) {
          const { data } = response;
          this.color = {
            ...response.data,
            values: response.data.values ?? []
          };
          res.isOk = true;
          res.msg = 'Цвет загружен';
        } else {
          console.error(
            `Error\nCode: ${response.status}\nStatus: ${response.statusText}`
          );
          this.color = null;
          res.isOk = false;
          res.msg = response.statusText;
        }
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        res.msg =
          error.response?.data?.message ||
          error.response?.statusText ||
          error.message ||
          'Unknown error';
      } else if (error instanceof Error) {
        res.msg = error.message;
      } else {
        res.msg = 'Unknown error';
      }
      res.isOk = false;
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };

  @action
  getColorValues = async (query: string) => {
    this.loading = true;
    try {
      const response = await api.colors.getColorValues(query);
      return response.data;
    } catch (error) {
      console.error('Failed to load characteristic values', error);
      return { data: [] };
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  @action
  addColor = async (params: {
    name: string;
    hex_code: string;
    hex_code_2?: string | null;
  }): Promise<IError> => {
    this.loading = true;

    const res = {
      isOk: false,
      msg: ''
    };

    try {
      const response = await api.colors.addColor(params);

      runInAction(() => {
        if (response.status === 201 || response.status === 200) {
          this.color = response.data;
          this.getColors({ page: 1, perpage: 20, paginate: true });
          res.isOk = true;
          res.msg = 'Цвет успешно добавлен';
        } else {
          console.error(
            `Error\nCode: ${response.status}\nStatus: ${response.statusText}`
          );
          res.isOk = false;
          res.msg = response.statusText || 'Ошибка при добавлении цвета';
        }
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        res.msg =
          error.response?.data?.message ||
          error.response?.statusText ||
          error.message ||
          'Unknown error';
      } else if (error instanceof Error) {
        res.msg = error.message;
      } else {
        res.msg = 'Unknown error';
      }
      res.isOk = false;
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };

  @action
  editColor = async (id: number, data: Partial<IColor>): Promise<IError> => {
    console.log('Отправляю в editColor:', data);
    this.loading = true;

    const res = {
      isOk: false,
      msg: ''
    };

    try {
      const response = await api.colors.editColor(id, data);

      runInAction(() => {
        if (response.status === 200 || response.status === 201) {
          res.isOk = true;
          res.msg = 'Цвет обновлён';
        } else {
          console.error(
            '❌ Статус не 200/201:',
            response.status,
            response.statusText
          );
          res.msg = response.statusText;
        }
      });
    } catch (error) {
      console.error('💥 Ошибка в editColor:', error); // 🔥
      res.msg = 'Ошибка сети или сервера';
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };

  @action
  deleteColor = async (id: number): Promise<IError> => {
    this.loading = true;

    const res = {
      isOk: false,
      msg: ''
    };

    try {
      const response = await api.colors.deleteColor(id);

      runInAction(() => {
        if (response.status === 200) {
          res.isOk = true;
          res.msg = 'Цвет удалён';
        } else {
          console.error(
            `Error\nCode: ${response.status}\nStatus: ${response.statusText}`
          );
          res.isOk = false;
          res.msg = response.statusText || 'Ошибка при удалении';
        }
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        res.msg =
          error.response?.data?.message ||
          error.response?.statusText ||
          error.message ||
          'Unknown error';
      } else if (error instanceof Error) {
        res.msg = error.message;
      } else {
        res.msg = 'Unknown error';
      }
      res.isOk = false;
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };

  @action
  clearAll = () => {
    this.colors = [];
    this.color = null;
  };

  @action
  changePositions = async (
    id1: number,
    id2: number,
    sort1: number,
    sort2: number
  ): Promise<IError> => {
    const res = { isOk: false, msg: '' };

    try {
      await api.colors.editColor(id1, { sort: sort1 });
      await api.colors.editColor(id2, { sort: sort2 });

      res.isOk = true;
      res.msg = 'Порядок изменён';
    } catch (error) {
      if (axios.isAxiosError(error)) {
        res.msg = `${error.message}:\n ${
          (error.response?.data as any)?.message || error.name
        }`;
      } else {
        res.msg = 'Ошибка при обновлении порядка';
      }
      res.isOk = false;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };
}

const colorsStore = new ColorsStore();
export default colorsStore;
